import type { DropSide } from "@ext/markdown/elements/gallery/edit/logic/galleryDrop";

interface Rect {
	left: number;
	top: number;
	width: number;
	height: number;
}

export type GalleryDropTarget =
	| { kind: "insert"; galleryPos: number; index: number; side: DropSide; rect: Rect; isReorder: boolean }
	| { kind: "merge"; targetPos: number; side: DropSide; rect: Rect };

export interface GalleryDropLabels {
	insert: string;
	merge: string;
}

const COLOR = "var(--color-primary-general)";
const BAR_WIDTH = 4;

const frameOf = (target: GalleryDropTarget): Rect => {
	const { rect } = target;
	if (target.kind === "merge") return rect;
	const left = target.side === "before" ? rect.left : rect.left + rect.width;
	return { left: left - BAR_WIDTH / 2, top: rect.top, width: BAR_WIDTH, height: rect.height };
};

const keyOf = (target: GalleryDropTarget | null) => {
	if (!target) return "";
	const { left, top, width, height } = frameOf(target);
	const reorder = target.kind === "insert" && target.isReorder;
	return [target.kind, reorder, Math.round(left), Math.round(top), Math.round(width), Math.round(height)].join(":");
};

class GalleryDropIndicator {
	private _element: HTMLElement | null = null;
	private _label: HTMLElement | null = null;
	private _key = "";

	constructor(private readonly _labels: GalleryDropLabels) {}

	show(target: GalleryDropTarget | null) {
		const key = keyOf(target);
		if (key === this._key) return;
		this._key = key;
		if (!target) return this._remove();

		const { left, top, width, height } = frameOf(target);
		const element = this._element ?? this._create();
		const isMerge = target.kind === "merge";

		Object.assign(element.style, {
			left: `${left}px`,
			top: `${top}px`,
			width: `${width}px`,
			height: `${height}px`,
			borderRadius: isMerge ? "0.75em" : `${BAR_WIDTH}px`,
			border: isMerge ? `2px solid ${COLOR}` : "none",
			background: isMerge ? `color-mix(in srgb, ${COLOR} 12%, transparent)` : COLOR,
		});
		this._label.textContent = isMerge ? this._labels.merge : this._labels.insert;
		this._label.hidden = target.kind === "insert" && target.isReorder;
	}

	destroy() {
		this._remove();
	}

	private _create() {
		const element = document.createElement("div");
		element.dataset.galleryDropIndicator = "";
		Object.assign(element.style, { position: "fixed", zIndex: "50", pointerEvents: "none" });

		const label = document.createElement("span");
		Object.assign(label.style, {
			position: "absolute",
			top: "0",
			left: "50%",
			transform: "translate(-50%, calc(-100% - 6px))",
			padding: "2px 8px",
			borderRadius: "999px",
			background: COLOR,
			color: "white",
			fontSize: "12px",
			lineHeight: "16px",
			whiteSpace: "nowrap",
		});

		element.appendChild(label);
		document.body.appendChild(element);
		this._element = element;
		this._label = label;
		return element;
	}

	private _remove() {
		this._element?.remove();
		this._element = null;
		this._label = null;
	}
}

export default GalleryDropIndicator;
